import type { ReactNode } from 'react';

/**
 * Los pocos íconos del sitio, como SVG de Lucide en línea: sin fuente de íconos y sin
 * `@mui/icons-material` en el árbol público (peso). Glifo de 20 px (24 en la caja de Lucide),
 * trazo 2, en `currentColor`: el área táctil de 48×48 la pone el contenedor, invisible.
 */

interface IconoProps {
  /** Lado del glifo en px. 20 es el estándar del sitio. */
  size?: number;
  /** Grosor del trazo. 2 es el estándar; los chevrones del paginado van más finos. */
  grosor?: number;
}

function Icono({ size = 20, grosor = 2, children }: IconoProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={grosor}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function IconoMenu(props: IconoProps) {
  return (
    <Icono {...props}>
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </Icono>
  );
}

export function IconoBuscar(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </Icono>
  );
}

export function IconoCerrar(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Icono>
  );
}

/**
 * Los chevrones del paginado de la home. Van al tamaño de la caja de Lucide (24) y con trazo 1,5:
 * son un glifo suelto, sin botón dibujado alrededor, y con trazo 2 se leen como una flecha pesada.
 */
export function IconoChevronIzquierda(props: IconoProps) {
  return (
    <Icono size={24} grosor={1.5} {...props}>
      <path d="m15 18-6-6 6-6" />
    </Icono>
  );
}

export function IconoChevronDerecha(props: IconoProps) {
  return (
    <Icono size={24} grosor={1.5} {...props}>
      <path d="m9 18 6-6-6-6" />
    </Icono>
  );
}

export function IconoPlay(props: IconoProps) {
  return (
    <Icono {...props}>
      <polygon points="6 3 20 12 6 21 6 3" fill="currentColor" stroke="none" />
    </Icono>
  );
}

export function IconoCopiar(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Icono>
  );
}

/** El de compartir del sistema, para el diálogo nativo del navegador (`navigator.share`). */
export function IconoCompartir(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
    </Icono>
  );
}

export function IconoFacebook(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </Icono>
  );
}

/**
 * El glifo real de WhatsApp (auricular dentro del globo), no un globo de diálogo genérico:
 * es el botón que más se usa para compartir y tiene que reconocerse de un vistazo.
 *
 * Va sólido —`fill` en `currentColor` y sin trazo, como `IconoPlay`— porque la marca no
 * existe en versión de línea y dibujarla con trazo 2 la deja irreconocible.
 */
export function IconoInstagram(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </Icono>
  );
}

export function IconoWhatsApp(props: IconoProps) {
  return (
    <Icono {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.21-8.24 8.21Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.5.11-.11.25-.29.37-.44.13-.14.17-.25.25-.41.09-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.84-.2-.49-.4-.42-.56-.43-.14-.01-.31-.01-.47-.01-.17 0-.44.06-.66.31-.23.25-.87.85-.87 2.07s.89 2.4 1.02 2.57c.12.16 1.75 2.67 4.24 3.75.59.26 1.05.41 1.41.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.11-.22-.17-.47-.29Z"
      />
    </Icono>
  );
}

export function IconoX(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
    </Icono>
  );
}
